import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Paper,
  Popper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { findTourTarget } from "./targets";
import type { PageTourStep } from "./types";

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function SpotlightTour({
  title,
  steps,
  onClose,
}: {
  title: string;
  steps: readonly PageTourStep[];
  onClose: () => void;
}) {
  const id = useId();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<TargetRect | null>(null);
  const card = useRef<HTMLDivElement | null>(null);
  const focusCard = useCallback((node: HTMLDivElement | null) => {
    card.current = node;
    node?.focus({ preventScroll: true });
  }, []);
  const stepIndex = Math.min(index, steps.length - 1);
  const current = steps[stepIndex];

  useLayoutEffect(() => {
    if (!current) return;
    const target = findTourTarget(current.target);
    target?.scrollIntoView?.({
      behavior: "instant",
      block: "center",
      inline: "nearest",
    });
    const measure = () => {
      const element = findTourTarget(current.target);
      if (!element) {
        setRect(null);
        return;
      }
      const bounds = element.getBoundingClientRect();
      const top = Math.max(8, bounds.top - 6);
      const left = Math.max(8, bounds.left - 6);
      const next = {
        top,
        left,
        width: Math.max(
          0,
          Math.min(window.innerWidth - 8, bounds.right + 6) - left
        ),
        height: Math.max(
          0,
          Math.min(window.innerHeight - 8, bounds.bottom + 6) - top
        ),
      };
      setRect((previous) =>
        previous &&
        previous.top === next.top &&
        previous.left === next.left &&
        previous.width === next.width &&
        previous.height === next.height
          ? previous
          : next
      );
    };
    measure();
    card.current?.focus({ preventScroll: true });
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    let frame = 0;
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    // Async sections above a target can move it without resizing the target.
    const mutations = new MutationObserver(scheduleMeasure);
    mutations.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["style", "class", "hidden"],
    });
    document.addEventListener("transitionend", scheduleMeasure, true);
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : undefined;
    if (target) observer?.observe(target);
    return () => {
      observer?.disconnect();
      mutations.disconnect();
      cancelAnimationFrame(frame);
      document.removeEventListener("transitionend", scheduleMeasure, true);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [current]);

  if (!current) return null;
  const last = stepIndex === steps.length - 1;
  const anchor =
    rect && rect.width > 0 && rect.height > 0
      ? {
          getBoundingClientRect: () =>
            new DOMRect(rect.left, rect.top, rect.width, rect.height),
        }
      : null;
  const content = (
    <Paper
      ref={focusCard}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} tutorial`}
      aria-describedby={`${id}-description`}
      tabIndex={-1}
      elevation={8}
      sx={{
        width: 340,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100dvh - 32px)",
        overflowY: "auto",
        p: 2.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        outline: "none",
        position: "relative",
      }}
    >
      <IconButton
        aria-label="Close tutorial"
        size="small"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography variant="caption" color="text.secondary" sx={{ pr: 3 }}>
        {title}
      </Typography>
      <Typography
        variant="subtitle1"
        component="h2"
        sx={{ fontWeight: 700, mt: 1, mb: 1, pr: 2 }}
      >
        {current.title}
      </Typography>
      <Typography
        id={`${id}-description`}
        variant="body2"
        color="text.secondary"
        sx={{ lineHeight: 1.6 }}
      >
        {current.description}
      </Typography>
      <Typography
        variant="caption"
        component="p"
        aria-live="polite"
        sx={{ mt: 2, mb: 1 }}
      >
        Step {stepIndex + 1} of {steps.length}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Button size="small" color="inherit" onClick={onClose}>
          Skip
        </Button>
        <Stack direction="row" spacing={1}>
          {stepIndex > 0 && (
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setIndex(stepIndex - 1)}
            >
              Back
            </Button>
          )}
          <Button
            size="small"
            variant="gradient"
            endIcon={last ? undefined : <ArrowForwardIcon />}
            onClick={() => (last ? onClose() : setIndex(stepIndex + 1))}
          >
            {last ? "Finish" : "Next"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  return (
    <Modal
      open
      hideBackdrop
      onClose={onClose}
      data-page-tour-overlay
      sx={(theme) => ({ zIndex: theme.zIndex.modal + 2 })}
    >
      <Box sx={{ position: "fixed", inset: 0, outline: "none" }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: anchor ? "transparent" : "rgba(0,0,0,0.55)",
          }}
        />
        {anchor && rect && (
          <Box
            aria-hidden="true"
            data-testid="tour-spotlight"
            sx={(theme) => ({
              position: "fixed",
              ...rect,
              pointerEvents: "none",
              borderRadius: 2,
              border: `2px solid ${theme.palette.primary.main}`,
              boxShadow: `0 0 0 9999px ${alpha(theme.palette.common.black, 0.55)}`,
            })}
          />
        )}
        {anchor ? (
          <Popper
            open
            disablePortal
            anchorEl={anchor}
            placement="right-start"
            modifiers={[
              { name: "offset", options: { offset: [0, 16] } },
              {
                name: "flip",
                options: {
                  padding: 16,
                  fallbackPlacements: ["left-start", "bottom", "top"],
                },
              },
              {
                name: "preventOverflow",
                options: { padding: 16, altAxis: true },
              },
            ]}
          >
            {content}
          </Popper>
        ) : (
          <Box
            sx={{
              position: "fixed",
              inset: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {content}
          </Box>
        )}
      </Box>
    </Modal>
  );
}
